import React, { useEffect, useState } from "react";
import Input from "sushiweb/InputTypes/Input";
import Button from "sushiweb/Button";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import {
  isValidated,
  getReadableDateTime,
  getInitialState,
  filterObj,
} from "../../../utils";
import * as StateActions from "../../../store/actions/State";
import * as CountryActions from "../../../store/actions/Country";
import * as UserActions from "../../../store/actions/User";
import InfiniteScrollDropdown from "../../Common/InfiniteScrollDropdown";
import Permission from "../../Common/Permission";
import MenuNames from "../../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../../constants/Menu/MenuActionsMappings";
import NoFooterModal from "../../Common/NoFooterModal";
import { BottomSectionModal } from "../../../constants/assets/containers";

const mandatoryFields = ["country_id", "name", "code"];

const StateFormModal = (props) => {
  const allActions = MenuActionsMappings[MenuNames.STATE];
  const [initialState, setInitialState] = useState({
    name: "",
    code: "",
    country_id: "",
    created_at: "",
    updated_at: "",
  });
  const {
    modalState,
    handleClose,
    activeId,
    activeState,
    activeFilters,
    filterFields,
    currentPage,
    setStatus,
  } = props;

  const handleSubmit = (evt) => {
    evt.preventDefault();

    // if filter button is clicked, then apply filters
    if (modalState === "Filter") {
      const filters = {};
      for (let key of filterFields) {
        if (initialState[key]) {
          filters[key] = initialState[key];
        }
      }
      props.getStateList(filters).then((res) => handleClose());
    } else {
      // check if all required fields are present
      const validatedState = isValidated(initialState, mandatoryFields);
      if (validatedState === "validated") {
        // only send the required fields in request body
        const body = filterObj(initialState, mandatoryFields);

        // send different requests for Edit and Add state.
        if (modalState === "Edit") {
          props.updateState(activeId, body).then((res) => {
            const pageRequest = { page: currentPage };
            props.getStateList(activeFilters, pageRequest);
            handleClose();
          });
        } else if (modalState === "Add") {
          props.createState(body).then((res) => {
            const pageRequest = { page: currentPage };
            props.getStateList(activeFilters, pageRequest);
            handleClose();
          });
        }
      } else {
        setStatus({ type: "error", message: validatedState });
      }
    }
  };

  // clears all applied filters.
  const handleClearFilters = () => {
    props.getStateList({}).then((res) => {
      props.resetState({ state: "activeFilters" });
      handleClose();
    });
  };

  const handleChange = (key, val) => {
    const values = { ...initialState };
    values[key] = val;
    setInitialState(values);
  };

  useEffect(() => {
    if (activeId) {
      props.getState(activeId);
    }

    // reset the state to show empty modal
    if (modalState === "Add" && Object.keys(activeState).length > 0) {
      props.resetState({ state: "activeState" });
    }
  }, [activeId, modalState]);

  useEffect(() => {
    // update the initialState on modal render for "Edit" state.
    if (modalState === "Edit" && Object.keys(activeState).length > 0) {
      const values = { ...initialState };
      Object.keys(values).forEach((key) => {
        values[key] = activeState[key]
          ? activeState[key]
          : getInitialState(initialState, key);
      });
      setInitialState(values);
    }
  }, [activeState]);

  useEffect(() => {
    // update the initialState on modal render for "Filter" state.
    if (Object.keys(activeFilters).length > 0 && modalState === "Filter") {
      const values = { ...initialState };
      Object.keys(values).forEach((key) => {
        values[key] = activeFilters[key]
          ? activeFilters[key]
          : getInitialState(initialState, key);
      });
      setInitialState(values);
    }
  }, [activeFilters]);

  const getRequiredButtonPerms = () => {
    if (modalState === "Edit") {
      return [allActions.UPDATE];
    } else if (modalState === "Add") {
      return [allActions.CREATE];
    }
    return undefined;
  };

  const isHiddenField = (key) => {
    if (modalState === "Filter") {
      return !filterFields.includes(key);
    } else if (modalState === "Add") {
      if (key === "created_at" || key === "updated_at") {
        return true;
      }
      return false;
    } else {
      return false;
    }
  };

  const formElements = [
    {
      key: "name",
      element: (
        <Input
          value={initialState.name}
          data-testid="stateName"
          id="sushi-input"
          onChange={(e) => handleChange("name", e)}
          label="Name"
        />
      ),
    },
    {
      key: "code",
      element: (
        <Input
          value={initialState.code}
          data-testid="stateCode"
          id="sushi-input"
          onChange={(e) => handleChange("code", e)}
          label="Code"
        />
      ),
    },
    {
      key: "country_id",
      element: (
        <InfiniteScrollDropdown
          id="stateCountryDropdown"
          priorityFlag
          getActiveList={props.getCountryList}
          nextPage={props.nextPageCountry}
          resKey="countries"
          modalState={modalState}
          keyName="country_id"
          labelKey="country_name"
          getDataLabelKey="country.country_name"
          keyTypeToSet="id"
          disabled={false}
          label="Country"
          handleChange={handleChange}
          remoteSearchApplicable={false}
          activeFilters={activeFilters}
          activeElement={activeState}
          getActiveItemById={props.getCountry}
          appliedFilters={{}}
        />
      ),
    },
    {
      key: "created_at",
      element: (
        <Input
          disabled
          value={getReadableDateTime(initialState.created_at)}
          data-testid="stateCreatedAt"
          id="sushi-input"
          onChange={(e) => handleChange("created_at", e)}
          label="Created At"
        />
      ),
    },
    {
      key: "updated_at",
      element: (
        <Input
          disabled
          value={getReadableDateTime(initialState.updated_at)}
          data-testid="stateUpdatedAt"
          id="sushi-input"
          onChange={(e) => handleChange("updated_at", e)}
          label="Updated At"
        />
      ),
    },
  ];

  return (
    <NoFooterModal
      header={true}
      heading={`${modalState} State`}
      handleClose={handleClose}
      id="StateFormModal"
      modalButtons={
        <BottomSectionModal className="flex items-center justify-between">
          {/* show the clear button when clicked on filters button */}
          {modalState === "Filter" && (
            <Button
              data-testid="filterFeatureBtn"
              onClick={handleClearFilters}
              className="sushi-btn"
            >
              Clear
            </Button>
          )}
          <Permission requiredPerms={getRequiredButtonPerms()}>
            <Button
              data-testid="submitButton"
              className="sushi-btn"
              onClick={handleSubmit}
            >
              {modalState === "Filter" ? "Apply" : "Submit"}
            </Button>
          </Permission>
        </BottomSectionModal>
      }
    >
      {formElements
        .filter((obj) => isHiddenField(obj.key) === false)
        .map((obj) => obj.element)}
    </NoFooterModal>
  );
};

const mapStateToProps = (state) => {
  const { State, Country } = state;
  return {
    activeState: State.activeState,
    activeFilters: State.activeFilters,
    currentPage: State.currentPage,
    nextPageCountry: Country.nextPage,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createState: bindActionCreators(StateActions.createState, dispatch),
    getStateList: bindActionCreators(StateActions.getStateList, dispatch),
    getState: bindActionCreators(StateActions.getState, dispatch),
    resetState: bindActionCreators(StateActions.resetState, dispatch),
    updateState: bindActionCreators(StateActions.updateState, dispatch),
    getCountryList: bindActionCreators(CountryActions.getCountryList, dispatch),
    getCountry: bindActionCreators(CountryActions.getCountry, dispatch),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(StateFormModal);
